import { formatPercent } from "../../helpers/quickbooks";
import { DatePickerInput } from "../DatePickerInput";
import { arrayHasItems } from "../../helpers/common";
import { fmt$, getDemandPlanningColumnLabel, PRESET_COLUMN_IDS } from "../../consts/demandplanning";
import { format, parseISO, startOfYear, subDays } from "date-fns";
import { DemandPlanningLoadingKeys, getDemandDistributionProfileData } from "../../slices/demandPlanningSlice";
import { LoaderSpinner } from "../LoaderSpinner";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { useIsMobile } from "../../helpers/useIsMobile";
import { CalendarDaysIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import SupplySku from "../SupplySku";

const DISTRIBUTION_PROFILE_COLUMN_IDS = PRESET_COLUMN_IDS["demand-distribution-profile"] || [];

const toIsoDay = (date) => format(date, "yyyy-MM-dd");

const buildRangePresets = () => {
    const today = new Date();
    const lastDays = (days) => ({
        start: toIsoDay(subDays(today, days - 1)),
        end: toIsoDay(today),
    });
    return [
        { label: "Last 30", ...lastDays(30) },
        { label: "Last 60", ...lastDays(60) },
        { label: "Last 90", ...lastDays(90) },
        { label: "Year to date", start: toIsoDay(startOfYear(today)), end: toIsoDay(today) },
    ];
};

const TIER_LABELS = ["Tier 1", "Tier 2", "Tier 3"];

const tierKey = (name) => String(name ?? "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");

/** Treat values in (0,1] as fractions; otherwise as whole percents (e.g. 40.53). */
const toPercentNumber = (v) => {
    if (v === null || v === undefined || v === "") return 0;
    const n = Number(v);
    if (!Number.isFinite(n)) return 0;
    if (n !== 0 && Math.abs(n) <= 1) return n * 100;
    return n;
};

const formatDeltaDisplay = (n) => {
    if (!Number.isFinite(n)) return "—";
    const sign = n > 0 ? "+" : "";
    return `${sign}${n.toFixed(2)}%`;
};

const deltaToneClass = (n) => {
    if (!Number.isFinite(n)) return "font-medium text-gray-700";
    if (n < 0) return "font-medium text-red-700";
    if (n > 0) return "font-medium text-emerald-700";
    return "font-medium text-gray-700";
};

const columnHeaderLabel = (id) => {
    if (id === "productName") return "Product";
    return getDemandPlanningColumnLabel(id);
};

const normalizeDistributionRow = (raw, idx) => {
    const revenue = Number(raw?.revenue ?? raw?.rangeRevenue ?? 0);
    const rangeSharePct =
        raw?.percentageOfRange != null && raw?.percentageOfRange !== ""
            ? Number(raw.percentageOfRange)
            : toPercentNumber(raw?.rangeShare ?? raw?.range_share ?? raw?.shareOfRange);
    const ltmSharePct =
        raw?.ltmShare != null && raw?.ltmShare !== ""
            ? Number(raw.ltmShare)
            : toPercentNumber(raw?.ltmPercent ?? raw?.ltm_share);
    const deltaRaw = raw?.delta ?? raw?.deltaPct;
    const deltaPoints = deltaRaw === null || deltaRaw === undefined || deltaRaw === ""
        ? NaN
        : Number(deltaRaw);

    const tierLabel = raw?.tierName ?? raw?.tier ?? "";

    return {
        key: String(raw?.supplySku ?? raw?.sku ?? raw?.id ?? idx),
        supplySku: raw?.supplySku ?? raw?.sku ?? "",
        productName: raw?.productName ?? raw?.product ?? "",
        subcategoryName: raw?.subcategoryName ?? raw?.subCategory ?? raw?.subcategory ?? "",
        revenue,
        rangeSharePct,
        ltmSharePct,
        deltaPoints,
        tier: tierLabel,
    };
};

const DemandDistributionProfileTable = ({
    startDistributionProfileDate,
    endDistributionProfileDate,
    selectedForecastDemandPlanning,
    handleStartDateChange,
    searchTerm,
    handleEndDateChange,
    handleRangeChange,
    selectedCategory,
    selectedSubcategory,
    selectedStatus,
    visibleColumnIds,
    mobileVisibleColumnIds,
    categoryFilterOptions,
    subcategoryFilterOptions,
}) => {

    const isMobile = useIsMobile();
    const [mobileDatesOpen, setMobileDatesOpen] = useState(false);

    const {
        demandDistributionProfileData,
        loadingKeys: demandDistributionProfileLoadingKeys,
    } = useSelector(state => state.demandPlanning);

    const dispatch = useDispatch();

    useEffect(() => {
        const categoryId = arrayHasItems(selectedCategory) ? Number(selectedCategory[0]) : null;
        const subcategoryId = arrayHasItems(selectedSubcategory) ? Number(selectedSubcategory[0]) : null;
        const status = arrayHasItems(selectedStatus) ? selectedStatus[0] : null;
        dispatch(getDemandDistributionProfileData({
            demandPlanningId: selectedForecastDemandPlanning?.id,
            demandPlanningYear: selectedForecastDemandPlanning?.forecastYear || selectedForecastDemandPlanning?.forecast_year,
            categoryId,
            subcategoryId,
            status,
            searchTerm,
            startDate: startDistributionProfileDate,
            endDate: endDistributionProfileDate,
        }));
    }, [
        selectedForecastDemandPlanning,
        selectedCategory,
        selectedSubcategory,
        selectedStatus,
        searchTerm,
        startDistributionProfileDate,
        endDistributionProfileDate,
    ]);

    const tierSummary = demandDistributionProfileData?.tierSummary ?? [];
    const distributions = demandDistributionProfileData?.distributions ?? [];
    const baseColumnOrder = DISTRIBUTION_PROFILE_COLUMN_IDS;
    const resolvedVisible = isMobile && arrayHasItems(mobileVisibleColumnIds)
        ? mobileVisibleColumnIds
        : arrayHasItems(visibleColumnIds)
            ? visibleColumnIds
            : baseColumnOrder;
    const columnsToShow = baseColumnOrder.filter((id) => resolvedVisible.includes(id));
    const stickySkuColumn = columnsToShow[0] === "supplySku" && columnsToShow.length > 1;

    const rawRows = arrayHasItems(distributions) ? distributions : [];
    const distributionRows = rawRows.map(normalizeDistributionRow);
    const maxRangeSharePct = distributionRows.reduce(
        (acc, row) => (Number.isFinite(row.rangeSharePct) && row.rangeSharePct > acc ? row.rangeSharePct : acc),
        0
    );
    const totalRangeRevenue = distributionRows.reduce((acc, row) => acc + (Number.isFinite(row.revenue) ? row.revenue : 0), 0);
    const formattedStartDate = startDistributionProfileDate ? format(parseISO(startDistributionProfileDate), 'MMM dd, yyyy') : "Start";
    const formattedEndDate = endDistributionProfileDate ? format(parseISO(endDistributionProfileDate), 'MMM dd, yyyy') : "End";
    const rangeLabel = `${formattedStartDate} → ${formattedEndDate}`;
    const normalizedCategory = arrayHasItems(selectedCategory) ? (categoryFilterOptions || []).find(option => option.value === Number(selectedCategory[0]))?.label : "";
    const normalizedSubcategory = arrayHasItems(selectedSubcategory) ? (subcategoryFilterOptions || []).find(option => option.value === Number(selectedSubcategory[0]))?.label : "";
    const scopeLabel = normalizedCategory
        ? normalizedCategory
        : normalizedSubcategory
        ? normalizedSubcategory
        : "All filtered records";

    const ddpDataIsLoading = demandDistributionProfileLoadingKeys.includes(DemandPlanningLoadingKeys.getDemandDistributionProfileData);

    const tierConcentration = TIER_LABELS.map((label) => {
        const match = tierSummary.find((item) => tierKey(item?.tierName) === tierKey(label));
        return {
            label,
            percent: match ? formatPercent(match.percentageOfTier) : "—",
        };
    });

    const today = toIsoDay(new Date());

    const rangePresets = buildRangePresets().map((preset) => ({
        label: preset.label,
        isActive:
            startDistributionProfileDate === preset.start &&
            endDistributionProfileDate === preset.end,
        onSelect: () => {
            if (handleRangeChange) {
                handleRangeChange({ start: preset.start, end: preset.end });
                return;
            }
            handleStartDateChange?.(preset.start);
            handleEndDateChange?.(preset.end);
        },
    }));

    return (
        <>
            <section className="flex-1 py-2 pt-0">
                    <div className="mb-2 rounded-lg border border-border bg-white p-1.5 shadow-sm md:mb-4 md:p-3">
                        <div className="rounded-lg border border-border bg-background/70 p-1.5 py-2 sm:p-3 sm:py-3">
                            {isMobile && (
                                <button
                                    type="button"
                                    onClick={() => setMobileDatesOpen(open => !open)}
                                    aria-expanded={mobileDatesOpen}
                                    className="flex w-full items-center justify-between gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm"
                                >
                                    <span className="flex min-w-0 items-center gap-2">
                                        <CalendarDaysIcon className="h-4 w-4 shrink-0 text-gray-500" />
                                        <span className="truncate">{rangeLabel}</span>
                                    </span>
                                    <ChevronDownIcon className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${mobileDatesOpen ? "rotate-180" : ""}`} />
                                </button>
                            )}
                            <div className={`flex-wrap items-end gap-2 ${isMobile ? (mobileDatesOpen ? "mt-2 flex" : "hidden") : "flex"}`}>
                                <div className="min-w-0 flex-1 sm:min-w-[220px] sm:flex-none">
                                    <p className="mb-1 text-[11px] font-semibold uppercase tracking-widest text-gray-400">
                                        Start date
                                    </p>
                                    <div className="w-full sm:w-52">
                                        <DatePickerInput
                                            value={startDistributionProfileDate}
                                            onChange={handleStartDateChange}
                                            placeholder="Start date"
                                            min={"2024-01-01"}
                                            max={endDistributionProfileDate || today}
                                            presets={rangePresets}
                                        />
                                    </div>
                                </div>
                                <div className="min-w-0 flex-1 sm:min-w-[220px] sm:flex-none">
                                    <p className="mb-1 text-[11px] font-semibold uppercase tracking-widest text-gray-400">
                                        End date
                                    </p>
                                    <div className="w-full sm:w-52">
                                        <DatePickerInput
                                            value={endDistributionProfileDate}
                                            onChange={handleEndDateChange}
                                            placeholder="End date"
                                            min={startDistributionProfileDate || "2024-01-01"}
                                            max={today}
                                            presets={rangePresets}
                                        />
                                    </div>
                                </div>
                                {!isMobile && tierConcentration.map((tier) => (
                                    <div key={tier.label} className="min-w-0 flex-1 sm:min-w-[96px] sm:flex-none">
                                        <p className="mb-1 text-[11px] font-semibold uppercase tracking-widest text-gray-400">
                                            {tier.label}
                                        </p>
                                        <p className="text-sm font-semibold text-gray-900">
                                            {tier.percent}
                                        </p>
                                    </div>
                                ))}
                            </div>
                            {ddpDataIsLoading ? (
                                <div className="flex justify-center items-center h-full py-6">
                                    <LoaderSpinner colorSpinner="black" width="70" height="70" border="3" />
                                </div>
                            ) : (
                                <div className="mt-2 md:mt-3">
                                    <div className="overflow-hidden rounded-lg border border-border bg-white">
                                        {!isMobile && (
                                            <div className="border-b border-border px-3 py-2">
                                                <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
                                                    SKU allocation profile
                                                </p>
                                                <p className="mt-1 text-sm text-gray-600">
                                                    {scopeLabel} · {rangeLabel} · {distributionRows.length} SKUs · {fmt$(totalRangeRevenue)} revenue in range.
                                                </p>
                                            </div>
                                        )}
                                        <div
                                            className="max-h-[570px] overflow-auto overscroll-x-contain"
                                            style={{ touchAction: "pan-x pan-y", WebkitOverflowScrolling: "touch" }}
                                        >
                                            {arrayHasItems(distributionRows) ? (
                                                <table className={`w-full min-w-full text-sm ${columnsToShow.length > 3 ? "sm:min-w-[760px]" : ""}`}>
                                                    <thead className="sticky top-0 z-10 bg-white">
                                                        <tr className="text-left text-[11px] font-semibold uppercase tracking-widest text-gray-400 whitespace-break-spaces">
                                                            {columnsToShow.map((colId) => (
                                                                <th
                                                                    key={colId}
                                                                    className={`px-3 py-2 font-semibold whitespace-break-spaces ${stickySkuColumn && colId === "supplySku" ? "sticky left-0 z-20 bg-white" : ""}`}
                                                                >
                                                                    {columnHeaderLabel(colId)}
                                                                </th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                    {distributionRows.map((row, idx) => (
                                                            <tr
                                                                key={row.key || idx}
                                                                className="border-y border-transparent hover:border-border"
                                                            >
                                                                {columnsToShow.map((colId) => (
                                                                    <td
                                                                        key={colId}
                                                                        className={`px-3 py-2 align-top ${stickySkuColumn && colId === "supplySku" ? "sticky left-0 z-[5] bg-white shadow-[1px_0_0_0_#e5e7eb]" : ""}`}
                                                                    >
                                                                        {colId === "supplySku" && (
                                                                            <SupplySku sku={row.supplySku} />
                                                                        )}
                                                                        {colId === "productName" && (
                                                                            <>
                                                                                <p className="font-medium text-gray-900">
                                                                                    {row.productName || "—"}
                                                                                </p>
                                                                                {row.subcategoryName ? (
                                                                                    <p className="mt-0.5 text-xs text-gray-600">
                                                                                        {row.subcategoryName}
                                                                                    </p>
                                                                                ) : null}
                                                                            </>
                                                                        )}
                                                                        {colId === "rangeRevenue" && (
                                                                            <span className="font-medium text-gray-900">
                                                                                {fmt$(
                                                                                    Number.isFinite(row.revenue)
                                                                                        ? row.revenue
                                                                                        : 0
                                                                                )}
                                                                            </span>
                                                                        )}
                                                                        {colId === "rangeShare" && (
                                                                            <div className="flex items-center gap-2 sm:gap-3">
                                                                                <div className="h-2 min-w-[60px] flex-1 overflow-hidden rounded-full bg-slate-100 sm:min-w-[100px] lg:min-w-[160px]">
                                                                                    <div
                                                                                        className="h-full rounded-full bg-slate-950"
                                                                                        style={{
                                                                                            width: `${
                                                                                                maxRangeSharePct > 0 &&
                                                                                                Number.isFinite(row.rangeSharePct)
                                                                                                    ? Math.min(
                                                                                                          100,
                                                                                                          Math.max(
                                                                                                              (row.rangeSharePct / maxRangeSharePct) * 100,
                                                                                                              1
                                                                                                          )
                                                                                                      )
                                                                                                    : 0
                                                                                            }%`,
                                                                                        }}
                                                                                    />
                                                                                </div>
                                                                                <span className="min-w-[52px] shrink-0 text-right font-medium tabular-nums text-gray-900 sm:min-w-[60px]">
                                                                                    {Number.isFinite(row.rangeSharePct)
                                                                                        ? `${row.rangeSharePct.toFixed(2)}%`
                                                                                        : "—"}
                                                                                </span>
                                                                            </div>
                                                                        )}
                                                                        {colId === "ltmShare" && (
                                                                            <span className="text-gray-700">
                                                                                {Number.isFinite(row.ltmSharePct)
                                                                                    ? `${row.ltmSharePct.toFixed(2)}%`
                                                                                    : "—"}
                                                                            </span>
                                                                        )}
                                                                        {colId === "delta" && (
                                                                            <span className={deltaToneClass(row.deltaPoints)}>
                                                                                {formatDeltaDisplay(row.deltaPoints)}
                                                                            </span>
                                                                        )}
                                                                        {colId === "tier" && (
                                                                            <span className="whitespace-nowrap text-sm font-medium text-gray-900">
                                                                                {row.tier || "—"}
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                ))}
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            ) : (
                                                <section className="bg-white p-3 shadow-sm h-full">
                                                    <div className="rounded-lg border border-dashed border-border text-center bg-background px-4 py-6 text-sm text-gray-600">
                                                        No demand distribution data is available for the current workbook-backed records.
                                                    </div>
                                                </section>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
            </section>
        </>
    )
}

export default DemandDistributionProfileTable;